import type { z } from 'zod';
import { ClientMessageSchema, type ClientMessage } from './client.js';
import {
  IceClientMessageSchema,
  IceServerMessageSchema,
  type IceClientMessage,
  type IceServerMessage,
} from './ice.js';
import { ServerMessageSchema, type ServerMessage } from './server.js';
import { MAX_CLIENT_MESSAGE_BYTES } from './version.js';

export type DecodeResult<T> =
  { ok: true; message: T } | { ok: false; error: { code: 'BAD_MESSAGE'; message: string } };

/** JSON text of a message. Both sides send messages as WebSocket text frames. */
export function encodeMessage(
  message: ClientMessage | ServerMessage | IceClientMessage | IceServerMessage,
): string {
  return JSON.stringify(message);
}

/**
 * Parses and validates a message from a client. Never throws. The length check counts UTF-16
 * units; the WebSocket server also enforces the byte limit on frames.
 */
export function decodeClientMessage(raw: string): DecodeResult<ClientMessage> {
  if (raw.length > MAX_CLIENT_MESSAGE_BYTES) return failure('Message too large');
  return decode(raw, ClientMessageSchema);
}

/** Parses and validates a message from a client on `/ice`. Never throws. */
export function decodeIceClientMessage(raw: string): DecodeResult<IceClientMessage> {
  if (raw.length > MAX_CLIENT_MESSAGE_BYTES) return failure('Message too large');
  return decode(raw, IceClientMessageSchema);
}

/** Parses and validates a message from the server on `/ice`. Never throws. */
export function decodeIceServerMessage(raw: string): DecodeResult<IceServerMessage> {
  return decode(raw, IceServerMessageSchema);
}

/** Parses and validates a message from the server. Never throws. */
export function decodeServerMessage(raw: string): DecodeResult<ServerMessage> {
  return decode(raw, ServerMessageSchema);
}

function decode<T>(raw: string, schema: z.ZodType<T>): DecodeResult<T> {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return failure('Message is not valid JSON');
  }
  const result = schema.safeParse(data);
  if (!result.success) {
    const issue = result.error.issues[0];
    const where = issue && issue.path.length > 0 ? ` at ${issue.path.join('.')}` : '';
    return failure(`Invalid message${where}: ${issue?.message ?? 'unknown error'}`);
  }
  return { ok: true, message: result.data };
}

function failure(message: string): { ok: false; error: { code: 'BAD_MESSAGE'; message: string } } {
  return { ok: false, error: { code: 'BAD_MESSAGE', message } };
}
